import { useState, type ChangeEvent } from "react";

import type { ItemType } from "../api/types";

const REF_NUMBER_PATTERN = /^[A-Za-z0-9.-]{1,20}$/;

export function isRefNumberValid(itemType: ItemType, refNumber: string): boolean {
  if (itemType === "email") {
    return true;
  }
  return REF_NUMBER_PATTERN.test(refNumber.trim());
}

interface ItemTypeSelectProps {
  itemType: ItemType;
  refNumber: string;
  onItemTypeChange: (itemType: ItemType) => void;
  onRefNumberChange: (value: string) => void;
}

const OPTIONS: Array<{ value: ItemType; label: string }> = [
  { value: "email", label: "Email" },
  { value: "rfi", label: "RFI" },
  { value: "submittal", label: "Submittal" },
];

export function ItemTypeSelect({
  itemType,
  refNumber,
  onItemTypeChange,
  onRefNumberChange,
}: ItemTypeSelectProps) {
  const [refTouched, setRefTouched] = useState(false);
  const needsRef = itemType === "rfi" || itemType === "submittal";
  const showRefError =
    needsRef && refTouched && !isRefNumberValid(itemType, refNumber);

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-slate-700">Item type</span>
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`cursor-pointer rounded-md border px-2 py-2 text-center text-sm transition-colors ${
              itemType === option.value
                ? "border-blue-600 bg-blue-50 text-blue-800"
                : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            <input
              type="radio"
              name="item-type"
              value={option.value}
              checked={itemType === option.value}
              onChange={() => onItemTypeChange(option.value)}
              className="sr-only"
            />
            <span className="block font-medium">{option.label}</span>
          </label>
        ))}
      </div>

      {needsRef && (
        <div className="flex flex-col gap-1">
          <label
            htmlFor="ref-number"
            className="text-sm font-medium text-slate-700"
          >
            {itemType === "rfi" ? "RFI number" : "Submittal number"}
          </label>
          <input
            id="ref-number"
            type="text"
            value={refNumber}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onRefNumberChange(event.target.value)
            }
            onBlur={() => setRefTouched(true)}
            aria-invalid={showRefError}
            aria-describedby={showRefError ? "ref-number-error" : undefined}
            placeholder="e.g. 042"
            className={`rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 ${
              showRefError
                ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                : "border-slate-300 focus:border-blue-500 focus:ring-blue-500"
            }`}
          />
          {showRefError && (
            <p id="ref-number-error" role="alert" className="text-xs text-red-600">
              Use 1–20 letters, numbers, periods, or hyphens.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
